import { useEffect, useState } from "react";
import { Users, RotateCcw, AlertCircle, CheckCircle2, Search, ChevronRight, Trash2 } from "lucide-react";
import { apiCall, ApiError } from "@/lib/api";

interface UserRow {
  userId: string;
  email: string;
  fullName: string | null;
  total: number;
  inProgress: number;
  completed: number;
  timedOut: number;
  bestScore: number | null;
  lastActivity: string | null;
}

interface AttemptRow {
  id: string;
  status: string;
  totalScore: number | null;
  finalBand: string | null;
  startedAt: string;
  endedAt: string | null;
  decisionCount: number;
}

interface Props {
  simId: string;
  simCode: string;
}

function fmtDate(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function statusToken(status: string): { color: string; bg: string; label: string } {
  switch (status) {
    case "completed":
      return { color: "#10b981", bg: "rgba(16,185,129,0.12)", label: "completed" };
    case "in_progress":
      return { color: "#f59e0b", bg: "rgba(245,158,11,0.12)", label: "in progress" };
    case "timed_out":
      return { color: "#f87272", bg: "rgba(248,114,114,0.12)", label: "timed out" };
    default:
      return { color: "#A78BFA", bg: "rgba(167,139,250,0.10)", label: status };
  }
}

/**
 * AttemptsAdminCard — per-user attempt admin for a sim.
 *
 * Two-level UX: outer table = one row per learner with aggregate counts +
 * a "Reset all" button. Click the row to expand → inner table of individual
 * attempts with per-attempt delete buttons. Backs Parking #1 v2: both
 * "reset everything for this learner" and "delete this one half-finished
 * attempt without wiping their good ones" are reachable in <=2 clicks.
 *
 * Compact column widths + monospace-numeric columns keep the table tight
 * on wide viewports; v1 sprawled because the column gaps weren't bounded.
 */
export function AttemptsAdminCard({ simId, simCode }: Props): JSX.Element {
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [resetting, setResetting] = useState<UserRow | null>(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [justReset, setJustReset] = useState<string | null>(null);

  // Per-user expansion: a single open userId at a time, with its attempts.
  const [openUserId, setOpenUserId] = useState<string | null>(null);
  const [openAttempts, setOpenAttempts] = useState<AttemptRow[] | null>(null);
  const [openLoading, setOpenLoading] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const [deletingAttempt, setDeletingAttempt] = useState<{ user: UserRow; attempt: AttemptRow } | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall<{ users: UserRow[] }>("/libraryAttemptsByUser", {
        method: "POST",
        body: { simId },
      });
      setUsers(res.users);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Couldn't load attempts");
    } finally {
      setLoading(false);
    }
  };

  const loadAttempts = async (userId: string): Promise<void> => {
    setOpenLoading(true);
    setOpenError(null);
    try {
      const res = await apiCall<{ attempts: AttemptRow[] }>("/libraryAttemptsForUser", {
        method: "POST",
        body: { simId, userId },
      });
      setOpenAttempts(res.attempts);
    } catch (err) {
      setOpenError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Couldn't load attempts");
    } finally {
      setOpenLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simId]);

  const toggleExpand = (u: UserRow): void => {
    if (openUserId === u.userId) {
      setOpenUserId(null);
      setOpenAttempts(null);
      return;
    }
    setOpenUserId(u.userId);
    setOpenAttempts(null);
    void loadAttempts(u.userId);
  };

  const handleReset = async (): Promise<void> => {
    if (resetting === null) return;
    setResetBusy(true);
    setResetError(null);
    try {
      const res = await apiCall<{ deleted: number }>("/libraryResetAttempts", {
        method: "POST",
        body: { simId, userId: resetting.userId },
      });
      setJustReset(`${resetting.email}: ${res.deleted} attempt${res.deleted === 1 ? "" : "s"} cleared`);
      setResetting(null);
      // Clear expansion if the reset target is open right now.
      if (openUserId === resetting.userId) {
        setOpenUserId(null);
        setOpenAttempts(null);
      }
      await load();
      setTimeout(() => setJustReset(null), 4000);
    } catch (err) {
      setResetError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Reset failed");
    } finally {
      setResetBusy(false);
    }
  };

  const handleDeleteAttempt = async (): Promise<void> => {
    if (deletingAttempt === null) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await apiCall("/libraryAttemptDelete", {
        method: "POST",
        body: { attemptId: deletingAttempt.attempt.id },
      });
      const u = deletingAttempt.user;
      setJustReset(`${u.email}: 1 attempt deleted`);
      setDeletingAttempt(null);
      // Refresh both the aggregate row and the expanded attempts.
      await load();
      if (openUserId === u.userId) await loadAttempts(u.userId);
      setTimeout(() => setJustReset(null), 4000);
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Delete failed");
    } finally {
      setDeleteBusy(false);
    }
  };

  const filtered = (users ?? []).filter((u) => {
    if (query.trim() === "") return true;
    const q = query.toLowerCase();
    return u.email.toLowerCase().includes(q) || (u.fullName ?? "").toLowerCase().includes(q);
  });

  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-200">
          <Users className="mr-1.5 inline h-3.5 w-3.5" /> Attempts ({users?.length ?? 0})
          <span className="ml-1 normal-case tracking-normal text-white/30">— per-user reset for {simCode}</span>
        </h2>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/35" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name or email"
            className="w-56 rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 pl-8 text-[12px] text-white placeholder:text-white/30 outline-none focus:border-fuchsia-400/60"
          />
        </div>
      </div>

      {justReset !== null && (
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-1.5 text-[11px] text-emerald-200">
          <CheckCircle2 className="h-3.5 w-3.5" /> {justReset}
        </div>
      )}

      {loading && <p className="mt-4 text-[12px] text-white/45">Loading attempts…</p>}
      {error !== null && (
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-rose-400/40 bg-rose-500/10 px-3 py-1.5 text-[11px] text-rose-200">
          <AlertCircle className="h-3.5 w-3.5" /> {error}
        </div>
      )}

      {!loading && users !== null && users.length === 0 && (
        <p className="mt-4 text-[12px] text-white/45">No learners have attempted this sim yet.</p>
      )}

      {!loading && filtered.length > 0 && (
        <div className="mt-3 overflow-hidden rounded-lg border border-white/8">
          <table className="w-full table-fixed text-[12px]">
            <colgroup>
              <col style={{ width: "2.5rem" }} />
              <col />
              <col style={{ width: "4rem" }} />
              <col style={{ width: "5rem" }} />
              <col style={{ width: "4rem" }} />
              <col style={{ width: "5rem" }} />
              <col style={{ width: "4rem" }} />
              <col style={{ width: "8.5rem" }} />
              <col style={{ width: "5.5rem" }} />
            </colgroup>
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.02] text-[10px] uppercase tracking-[0.12em] text-white/40">
                <th className="px-1 py-2"></th>
                <th className="px-2 py-2 text-left">Learner</th>
                <th className="px-2 py-2 text-right">Total</th>
                <th className="px-2 py-2 text-right">In&nbsp;prog</th>
                <th className="px-2 py-2 text-right">Done</th>
                <th className="px-2 py-2 text-right">Timed&nbsp;out</th>
                <th className="px-2 py-2 text-right">Best</th>
                <th className="px-2 py-2 text-left">Last activity</th>
                <th className="px-2 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const isOpen = openUserId === u.userId;
                return (
                  <RowGroup key={u.userId}>
                    <tr
                      onClick={() => toggleExpand(u)}
                      className="cursor-pointer border-b border-white/5 hover:bg-white/[0.03]"
                    >
                      <td className="px-1 py-2 text-center text-white/40">
                        <ChevronRight className={`inline h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                      </td>
                      <td className="px-2 py-2 truncate">
                        <div className="truncate font-medium text-white">{u.fullName ?? u.email.split("@")[0]}</div>
                        <div className="truncate text-[10.5px] text-white/45">{u.email}</div>
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-white/80">{u.total}</td>
                      <td className="px-2 py-2 text-right font-mono text-amber-200/85">{u.inProgress > 0 ? u.inProgress : "—"}</td>
                      <td className="px-2 py-2 text-right font-mono text-emerald-200/85">{u.completed > 0 ? u.completed : "—"}</td>
                      <td className="px-2 py-2 text-right font-mono text-rose-200/70">{u.timedOut > 0 ? u.timedOut : "—"}</td>
                      <td className="px-2 py-2 text-right font-mono text-white/80">{u.bestScore !== null ? u.bestScore : "—"}</td>
                      <td className="px-2 py-2 text-[11.5px] text-white/55">{fmtDate(u.lastActivity)}</td>
                      <td className="px-2 py-2 text-right">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setResetting(u); setResetError(null); }}
                          className="inline-flex items-center gap-1 rounded-md border border-rose-400/30 bg-rose-500/10 px-2 py-1 text-[10.5px] font-semibold text-rose-200 transition-colors hover:border-rose-400/60 hover:bg-rose-500/20"
                          title="Delete every attempt by this learner"
                        >
                          <RotateCcw className="h-3 w-3" /> Reset all
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b border-white/5 bg-white/[0.02]">
                        <td></td>
                        <td colSpan={8} className="px-2 py-3">
                          <ExpandedAttempts
                            loading={openLoading}
                            error={openError}
                            attempts={openAttempts}
                            onDeleteAttempt={(a) => { setDeletingAttempt({ user: u, attempt: a }); setDeleteError(null); }}
                          />
                        </td>
                      </tr>
                    )}
                  </RowGroup>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && users !== null && users.length > 0 && filtered.length === 0 && (
        <p className="mt-4 text-[12px] text-white/45">No learner matches that filter.</p>
      )}

      {/* Reset-all confirm modal */}
      {resetting !== null && (
        <ConfirmModal
          tone="rose"
          title="Reset all attempts?"
          busy={resetBusy}
          error={resetError}
          onCancel={() => setResetting(null)}
          onConfirm={() => void handleReset()}
          confirmLabel={`Reset ${resetting.total} attempt${resetting.total === 1 ? "" : "s"}`}
          confirmIcon={<RotateCcw className="h-3.5 w-3.5" />}
        >
          <p>
            This permanently deletes <span className="font-bold text-rose-200">{resetting.total} attempt{resetting.total === 1 ? "" : "s"}</span> by{" "}
            <span className="font-medium">{resetting.fullName ?? resetting.email}</span> on <span className="font-mono text-rose-200">{simCode}</span>.
          </p>
          <ul className="mt-3 space-y-1 text-[11.5px] text-white/55">
            <li>• {resetting.completed} completed, {resetting.inProgress} in progress, {resetting.timedOut} timed out</li>
            <li>• All scored answers (attempt_responses) cascade with it</li>
            <li>• The learner can start fresh immediately</li>
            <li>• Leaderboard / replay history updates accordingly</li>
          </ul>
        </ConfirmModal>
      )}

      {/* Per-attempt delete confirm modal */}
      {deletingAttempt !== null && (
        <ConfirmModal
          tone="rose"
          title="Delete this attempt?"
          busy={deleteBusy}
          error={deleteError}
          onCancel={() => setDeletingAttempt(null)}
          onConfirm={() => void handleDeleteAttempt()}
          confirmLabel="Delete attempt"
          confirmIcon={<Trash2 className="h-3.5 w-3.5" />}
        >
          <p>
            One attempt by <span className="font-medium">{deletingAttempt.user.fullName ?? deletingAttempt.user.email}</span> will be deleted:
          </p>
          <ul className="mt-3 space-y-1 text-[11.5px] text-white/55">
            <li>• Status: <span className="font-mono">{deletingAttempt.attempt.status}</span></li>
            <li>• Started: {fmtDate(deletingAttempt.attempt.startedAt)}</li>
            <li>• {deletingAttempt.attempt.decisionCount} scored decision{deletingAttempt.attempt.decisionCount === 1 ? "" : "s"}{deletingAttempt.attempt.totalScore !== null ? `, score ${deletingAttempt.attempt.totalScore}` : ""}</li>
            <li>• Their other attempts on this sim are NOT touched</li>
          </ul>
        </ConfirmModal>
      )}
    </>
  );
}

/** Tiny wrapper that lets us return two <tr> siblings from a list. */
function RowGroup({ children }: { children: React.ReactNode }): JSX.Element {
  return <>{children}</>;
}

function ExpandedAttempts({
  loading,
  error,
  attempts,
  onDeleteAttempt,
}: {
  loading: boolean;
  error: string | null;
  attempts: AttemptRow[] | null;
  onDeleteAttempt: (a: AttemptRow) => void;
}): JSX.Element {
  if (loading) return <p className="text-[11.5px] text-white/45">Loading attempts…</p>;
  if (error !== null) {
    return (
      <span className="inline-flex items-center gap-1 text-[11.5px] text-rose-300">
        <AlertCircle className="h-3 w-3" /> {error}
      </span>
    );
  }
  if (attempts === null || attempts.length === 0) {
    return <p className="text-[11.5px] text-white/45">No attempts.</p>;
  }
  return (
    <div className="space-y-1.5">
      {attempts.map((a) => {
        const tok = statusToken(a.status);
        return (
          <div
            key={a.id}
            className="grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-3 rounded-md border border-white/8 bg-white/[0.02] px-3 py-2"
          >
            <span className="text-[10.5px] font-mono text-white/40">{a.id.slice(0, 8)}…</span>
            <span
              className="rounded-full border px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.1em]"
              style={{ background: tok.bg, borderColor: `${tok.color}66`, color: tok.color }}
            >
              {tok.label}
            </span>
            <span className="font-mono text-[11.5px] text-white/75">
              {a.decisionCount}d{a.totalScore !== null ? ` · ${a.totalScore}` : ""}
              {a.finalBand !== null ? ` · ${a.finalBand}` : ""}
            </span>
            <span className="text-[11px] text-white/45">
              {fmtDate(a.startedAt)}{a.endedAt !== null ? ` → ${fmtDate(a.endedAt)}` : ""}
            </span>
            <button
              type="button"
              onClick={() => onDeleteAttempt(a)}
              className="inline-flex items-center gap-1 rounded-md border border-rose-400/30 bg-rose-500/10 px-2 py-1 text-[10.5px] font-semibold text-rose-200 transition-colors hover:border-rose-400/60 hover:bg-rose-500/20"
              title="Delete this attempt only"
            >
              <Trash2 className="h-3 w-3" /> Delete
            </button>
          </div>
        );
      })}
    </div>
  );
}

function ConfirmModal({
  title, tone, children, busy, error, onCancel, onConfirm, confirmLabel, confirmIcon,
}: {
  title: string;
  tone: "rose";
  children: React.ReactNode;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  confirmIcon: JSX.Element;
}): JSX.Element {
  const border = tone === "rose" ? "border-rose-400/30" : "border-white/15";
  return (
    <div
      onClick={() => { if (!busy) onCancel(); }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-full max-w-md overflow-hidden rounded-2xl border ${border} bg-gradient-to-br from-[#1a0a16] to-[#0c0810] text-white shadow-2xl`}
      >
        <div className="border-b border-white/10 px-5 py-4">
          <h2 className="flex items-center gap-2 text-base font-bold text-rose-200">
            {confirmIcon} {title}
          </h2>
        </div>
        <div className="px-5 py-4 text-[13px] leading-relaxed text-white/80">
          {children}
          {error !== null && (
            <div className="mt-3 rounded-lg border border-rose-400/40 bg-rose-500/15 px-3 py-2 text-[12px] text-rose-200">{error}</div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-white/10 px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-[12px] text-white/70 hover:border-white/30 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-rose-400/40 bg-rose-500/20 px-4 py-1.5 text-[12px] font-semibold text-rose-100 hover:border-rose-400/60 hover:bg-rose-500/30 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {confirmIcon} {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
